import React, { useState, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import './LeaveDaysModal.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Calendar editor for marking which days of a month are OFF (leave) for one branch.
// Working days = calendar days − leave days. Used by the MTD distribution pace math.
const LeaveDaysModal = ({ branchId, branchName, year, month, calendarDays, initialLeaveDays = [], onClose, onSaved, canReset = true }) => {
    const [leaveDays, setLeaveDays] = useState(() => new Set(initialLeaveDays));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // First weekday of the month (0=Sun) so the grid lines up under the right column.
    const firstWeekday = useMemo(() => new Date(year, month - 1, 1).getDay(), [year, month]);

    const toggle = (d) => {
        setLeaveDays(prev => {
            const next = new Set(prev);
            if (next.has(d)) next.delete(d); else next.add(d);
            return next;
        });
    };

    // Manual quick-fill: select every occurrence of a given weekday (e.g. all Fridays).
    const markWeekday = (weekday) => {
        setLeaveDays(prev => {
            const next = new Set(prev);
            for (let d = 1; d <= calendarDays; d++) {
                if (new Date(year, month - 1, d).getDay() === weekday) next.add(d);
            }
            return next;
        });
    };

    const clearAll = () => setLeaveDays(new Set());

    const workingDays = calendarDays - leaveDays.size;

    const save = async () => {
        try {
            setSaving(true);
            setError(null);
            const payload = {
                organizationId: branchId,
                year,
                month,
                leaveDays: [...leaveDays].sort((a, b) => a - b)
            };
            await axiosInstance.put('/api/sales-target/working-days', payload);
            onSaved && onSaved();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save leave days');
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm('Clear all leave days and reset this branch to the full calendar?')) return;
        try {
            setSaving(true);
            setError(null);
            await axiosInstance.delete('/api/sales-target/working-days', {
                data: { organizationId: branchId, year, month }
            });
            onSaved && onSaved();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset');
        } finally {
            setSaving(false);
        }
    };

    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= calendarDays; d++) cells.push(d);

    return (
        <div className="lvm-overlay" onClick={onClose}>
            <div className="lvm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lvm-header">
                    <div>
                        <div className="lvm-title">Leave Days — {branchName}</div>
                        <div className="lvm-subtitle">{WEEKDAYS[0]} … click a day to mark it OFF</div>
                    </div>
                    <button className="lvm-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                <div className="lvm-quick">
                    {WEEKDAYS.map((w, i) => (
                        <button key={w} className="lvm-quick-btn" onClick={() => markWeekday(i)} title={`Mark all ${w}s as leave`}>
                            + {w}
                        </button>
                    ))}
                    <button className="lvm-quick-btn lvm-quick-clear" onClick={clearAll}>Clear</button>
                </div>

                <div className="lvm-grid lvm-grid-head">
                    {WEEKDAYS.map(w => <div key={w} className="lvm-dow">{w}</div>)}
                </div>
                <div className="lvm-grid">
                    {cells.map((d, idx) => d === null
                        ? <div key={`e${idx}`} className="lvm-cell lvm-empty" />
                        : (
                            <button
                                key={d}
                                className={`lvm-cell${leaveDays.has(d) ? ' lvm-leave' : ''}`}
                                onClick={() => toggle(d)}
                            >
                                {d}
                            </button>
                        )
                    )}
                </div>

                <div className="lvm-summary">
                    <span>Working days: <strong>{workingDays}</strong></span>
                    <span>Leave days: <strong>{leaveDays.size}</strong></span>
                    <span>Calendar: <strong>{calendarDays}</strong></span>
                </div>

                {error && <div className="lvm-error">⚠ {error}</div>}

                <div className="lvm-actions">
                    {canReset
                        ? <button className="lvm-btn lvm-btn-reset" onClick={reset} disabled={saving}>Reset to calendar</button>
                        : <span />}
                    <div className="lvm-actions-right">
                        <button className="lvm-btn lvm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                        <button className="lvm-btn lvm-btn-save" onClick={save} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeaveDaysModal;
